'use client';

import {
  Button,
  ButtonGroup,
  Flex,
  Dialog,
  UploadBox,
  File,
  Select,
  SelectItem,
  Alert,
} from '@/_components';
import type { QuestionFormUploadProps } from './QuestionFormUpload.types';
import { QUESTION_SEPARATOR } from '@/_constants/questions';
import { UserContext } from '@/_contexts/UserProvider';
import { getIsPredefinedAnswer } from '@/_utils/questions';
import {
  type FC,
  useContext,
  useMemo,
  useCallback,
  useState,
  type ChangeEvent,
  useEffect,
} from 'react';
import { deletePath } from '@/_api/client-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { handleAcceptedUploadedFiles } from '@/_utils/files';
import { getUploadedItems, getShortUploadName } from '@/_utils/answers';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { Questionnaire } from '@/_types/models/questionnaire';
import { getRealDocumentPath } from '@/_utils/documents';
import { getUploadPath } from '@/_utils/questionnaire';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { getExtractedDocumentByPath } from '@/_api/integrations';

// NOTE: if you make changes here, you'll probably need to also update QuestionFormUpload in QuestionnaireDocumentChecklistSection
export const QuestionFormUpload: FC<QuestionFormUploadProps> = ({
  question,
  onChange,
  isDisabled,
  mode,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token, selectedCompanyId } = useContext(UserContext);
  const {
    reloadQuestions,
    selectedQuestionnaire,
    selectedQuestionnaireWithAllFields,
    updateQuestion,
  } = useContext(ClientQuestionnaireContext);
  const {
    updatePreviewQuestion,
    previewQuestionnaire,
    previewCompanyId,
    setPreviewFile,
  } = useContext(PreviewContext);
  const { previewTemplate, updateTemplateQuestion } =
    useContext(UserPreviewContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [uploadOption, setUploadOption] = useState<string>('');
  const [disabledFiles, setDisabledFiles] = useState<Record<string, boolean>>(
    {},
  );
  const [hasSplitDocument, setHasSplitDocument] = useState<boolean>(false);

  const uploadedFileAnswers: Array<string> = useMemo(
    () => getUploadedItems(question?.answer),
    [question?.answer],
  );

  const hasUploadOptions: boolean = useMemo(
    () => question?.options?.length > 0,
    [question?.options],
  );
  const isPredefinedAnswer: boolean = getIsPredefinedAnswer(question);

  const checkButtonDisabled = async (fileName: string) => {
    const isDisabled = await getIsSplitDocument(fileName);
    setDisabledFiles((prev) => ({ ...prev, [fileName]: isDisabled }));
  };

  const companyId: number | null | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewCompanyId;
    if (mode === 'admin-template') return -1;

    return selectedCompanyId;
  }, [mode, previewCompanyId, selectedCompanyId]);

  const questionnaire: Questionnaire | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestionnaire;
    if (mode === 'admin-template') return previewTemplate as any;

    return selectedQuestionnaireWithAllFields || selectedQuestionnaire;
  }, [
    mode,
    previewTemplate,
    previewQuestionnaire,
    selectedQuestionnaire,
    selectedQuestionnaireWithAllFields,
  ]);

  const uploadPath: string = questionnaire ? getUploadPath(questionnaire) : '';

  useEffect(() => {
    const isAnyFileDisabled = uploadedFileAnswers?.some(
      (fileName) => !!disabledFiles[fileName],
    );

    if (isAnyFileDisabled) {
      setHasSplitDocument(true);
    } else {
      setHasSplitDocument(false);
    }
  }, [uploadedFileAnswers, disabledFiles]);

  const onDrop = useCallback(
    async (acceptedFiles: Array<File>) => {
      if (!token) return;

      const uploadedPaths: Array<string> = [...uploadedFileAnswers];
      const section = questionnaire?.sections.find(
        (s) => s.id === question.section_id,
      );

      let responseOfFiles: any;

      try {
        setLoading(true);
        setOpen(false);

        responseOfFiles = await handleAcceptedUploadedFiles({
          acceptedFiles,
          addToast,
          companyId: (companyId || questionnaire?.company_id)!,
          description: '',
          questionnaireId: questionnaire?.id!,
          selectedOptions:
            hasUploadOptions && !!uploadOption ? [uploadOption] : undefined,
          successfullyUploadedFileCallback: (file) =>
            uploadedPaths.push(`${uploadPath}/${file?.name}`),
          tags:
            section && preferences.document_tags_enabled ? [section.name] : [],
          token,
          uploadPath,
        });
      } finally {
        responseOfFiles &&
          responseOfFiles.forEach((file: any) => {
            const { original_document_name, document_name } = file || {};

            if (original_document_name !== document_name) {
              // Find the original document name with the document name in uploadedPaths
              const index = uploadedPaths.indexOf(
                `${uploadPath}/${original_document_name}`,
              );

              // If we find a match, that means it needs to be replaced.
              if (index !== -1) {
                uploadedPaths[index] = `${uploadPath}/${document_name}`;
              }
            }
          });

        const uploadedPath: string = uploadedPaths.join(QUESTION_SEPARATOR);

        onChange?.(uploadedPath);

        const answerParams = {
          questionId: question?.id!,
          answer: uploadedPath,
        };

        if (mode === 'admin-preview') await updatePreviewQuestion(answerParams);
        else if (mode === 'admin-template') {
          updateTemplateQuestion(answerParams);
        } else await updateQuestion(answerParams);

        // We need to call this because we need to get the newest `extracted_documents` back asap so that if the user wants to preview their uploaded document, it will be available. We only allow previewing based upon the documents (not the question answer).
        await reloadQuestions();

        setLoading(false);
      }
    },
    [
      addToast,
      companyId,
      hasUploadOptions,
      mode,
      onChange,
      question,
      questionnaire?.company_id,
      questionnaire?.id,
      reloadQuestions,
      token,
      updatePreviewQuestion,
      updateQuestion,
      updateTemplateQuestion,
      uploadOption,
      uploadPath,
      uploadedFileAnswers,
      preferences.document_tags_enabled,
      questionnaire?.sections,
    ],
  );

  const onToggleUpload = () => setOpen(!isOpen);

  const onDeleteFile = async (fileName: string) => {
    if (!fileName) return;

    await deletePath({
      companyId: companyId!,
      path: `/${fileName}`,
      token,
    });

    const newFiles: Array<string> = uploadedFileAnswers.filter(
      (f) => f !== fileName,
    );
    const newFilesPath: string = !!newFiles?.length
      ? newFiles.join(QUESTION_SEPARATOR)
      : '';

    if (mode === 'admin-preview') {
      updatePreviewQuestion({
        questionId: question?.id!,
        answer: newFilesPath,
      });
    } else if (mode === 'admin-template') {
      updateTemplateQuestion({
        questionId: question?.id!,
        answer: newFilesPath,
      });
    } else {
      updateQuestion({
        questionId: question?.id!,
        answer: newFilesPath,
      });
    }
  };

  const onPreview = async (fileName: string) => {
    const realDocumentUrl: string | undefined = await getRealDocumentPath(
      questionnaire!,
      fileName,
      token!,
    );

    return setPreviewFile(realDocumentUrl);
  };

  const uploadOptions: Array<SelectItem> = useMemo(() => {
    const emptyItem = { name: 'Please select document option', value: '' };
    const optionItems: Array<SelectItem> =
      question?.options?.map((option) => ({ name: option, value: option })) ||
      [];

    return [emptyItem, ...optionItems];
  }, [question?.options]);

  const onUploadOptionSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const newOption: string = event.target.value;

    setUploadOption(newOption);
  };

  const getIsSplitDocument = async (fileName: string): Promise<boolean> => {
    if (!companyId || !question.answer) {
      return false;
    }

    try {
      const response = await getExtractedDocumentByPath({
        companyId,
        path: fileName,
        token,
      });

      return !!response.document?.parent_document_id;
    } catch (error) {
      console.error('Error checking if document is split:', error);
      return false;
    }
  };

  return (
    <>
      <Dialog isOpen={isOpen} title="Upload" onClose={onToggleUpload}>
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            {hasUploadOptions && (
              <Select
                label="Document Options"
                placeholder="Select Document Option"
                items={uploadOptions}
                shouldAutoSelectIfSingle={true}
                selectedValue={uploadOption}
                onChange={onUploadOptionSelect}
              />
            )}
            <UploadBox
              onDrop={onDrop}
              isDisabled={hasUploadOptions && !uploadOption}
              isLoading={false || !questionnaire}
              allowMultiple={true}
            />
          </Flex>
        </Dialog.Content>
      </Dialog>
      <Flex isColumn={true} gap={4}>
        {hasSplitDocument && (
          <Alert type="success" icon="check-circle">
            Marked as completed by firm. Use the upload button below to provide
            additional documents.
          </Alert>
        )}
        {question?.answer &&
          !isPredefinedAnswer &&
          uploadedFileAnswers?.map((fileName, key) => {
            if (disabledFiles[fileName] === undefined) {
              checkButtonDisabled(fileName);
            }

            const shortUploadAnswer: string = getShortUploadName(fileName);
            const onDelete = (): any => onDeleteFile(fileName);
            const hasNoAccess: boolean = !!disabledFiles[fileName];

            return (
              !hasNoAccess && (
                <File title={shortUploadAnswer} key={key}>
                  <Button
                    onClick={() => onPreview(fileName)}
                    value="Preview"
                    iconLeft="eye"
                    size="tiny"
                    intent="ghost"
                    isIconOnly={true}
                    withTooltip={true}
                  />
                  <Button
                    value="Delete"
                    onClick={onDelete}
                    intent="danger-secondary"
                    size="tiny"
                    isDisabled={isDisabled}
                    iconLeft="trash"
                    isIconOnly={true}
                    withTooltip={true}
                  />
                </File>
              )
            );
          })}
        <ButtonGroup>
          <Button
            size="small"
            intent="primary"
            iconLeft="upload"
            value="Upload"
            isLoading={isLoading}
            isDisabled={isLoading || isDisabled}
            onClick={onToggleUpload}
          />
        </ButtonGroup>
      </Flex>
    </>
  );
};
